// src/belediye/components/reports/ReportHelpDrawer.js
//
// Report V1 – Yardım/Bilgi içeriği
// - İçerik (ReportHelpContent) ayrı export edildi → hem eski modal (ReportHelpDrawer)
//   hem de yeni "Bilgi" popover/bottom-sheet (PageHeader.infoContent) aynı içeriği kullanır.
// - Başkan / Müdür / Birim Personeli için ayrı anlatım
// - Tablolar, kolonlar, filtreler, export ve kullanım akışı detaylı anlatılır

import React, { useCallback, useEffect, useMemo } from "react";
import {
  XMarkIcon,
  InformationCircleIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  ShieldCheckIcon,
  TableCellsIcon,
  ClipboardDocumentListIcon,
  DocumentCheckIcon,
  CheckCircleIcon,
  UserCircleIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

import "./ReportHelpDrawer.css";

/* =============================================================================
   Small UI helpers
============================================================================= */

const Section = ({ title, icon: Icon, children, subtle = false }) => (
  <section className={`reportv1-helpv2-section ${subtle ? "is-subtle" : ""}`}>
    <div className="reportv1-helpv2-section__head">
      {Icon ? <Icon className="reportv1-helpv2-section__icon" /> : null}
      <h3 className="reportv1-helpv2-section__title">{title}</h3>
    </div>
    <div className="reportv1-helpv2-section__body">{children}</div>
  </section>
);

const Bullet = ({ children }) => (
  <li className="reportv1-helpv2-bullet">{children}</li>
);

const Chip = ({ children }) => (
  <span className="reportv1-helpv2-chip">{children}</span>
);

const InfoRow = ({ label, value }) => (
  <div className="reportv1-helpv2-info-row">
    <div className="reportv1-helpv2-info-row__label">{label}</div>
    <div className="reportv1-helpv2-info-row__value">{value}</div>
  </div>
);

const RoleCard = ({ title, icon: Icon, items }) => (
  <div className="reportv1-helpv2-rolecard">
    <div className="reportv1-helpv2-rolecard__head">
      {Icon ? <Icon className="reportv1-helpv2-rolecard__icon" /> : null}
      <div className="reportv1-helpv2-rolecard__title">{title}</div>
    </div>
    <ul className="reportv1-helpv2-list">
      {items.map((item, idx) => (
        <Bullet key={`${title}-${idx}`}>{item}</Bullet>
      ))}
    </ul>
  </div>
);

const ColumnTable = ({ rows }) => (
  <div className="reportv1-helpv2-tablewrap">
    <table className="reportv1-helpv2-table">
      <thead>
        <tr>
          <th>Sütun</th>
          <th>Anlamı</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr key={idx}>
            <td>{row.name}</td>
            <td>{row.desc}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* =============================================================================
   İçerik — hem modal hem "Bilgi" popover/bottom-sheet burayı kullanır
============================================================================= */

export const ReportHelpContent = ({ config }) => {
  const visibility = config?.visibility || {};
  const choices = config?.choices || {};

  const canLogs = !!visibility?.logs_enabled;
  const canMultiUnit = !!visibility?.unit_multi_select;
  const closeApprovalEnabled = !!visibility?.close_approval_enabled;

  const exportModes = useMemo(() => {
    const modes = Array.isArray(choices?.export_modes) ? choices.export_modes : [];
    return modes.map((m) =>
      m === "full" ? "Detaylı Excel (çoklu sheet)" : "Liste Excel (tek sheet / temel çıktı)"
    );
  }, [choices?.export_modes]);

  const applicationColumns = [
    { name: "Takip No", desc: "Başvurunun sistemdeki benzersiz takip numarasıdır." },
    { name: "Oluşturma Tarihi", desc: "Başvurunun sisteme ilk giriş tarihidir." },
    { name: "Birim", desc: "Başvurunun mevcut bağlı olduğu birimdir." },
    { name: "Durum", desc: "Başvurunun güncel işlem durumudur: yeni, incelemeye alındı, çözüldü, çözülemedi gibi." },
    { name: "Mahalle", desc: "Başvurunun kayıtlı mahalle bilgisidir." },
    { name: "İncelemeye Alan", desc: "Başvuruyu ilk işleme alan kullanıcıdır." },
    { name: "İnceleme Tarihi", desc: "Başvurunun işleme alındığı tarihtir." },
    { name: "Sonuçlandıran", desc: "Başvuruyu çözüldü / çözülemedi durumuna getiren kullanıcıdır." },
    { name: "Sonuçlandırma Tarihi", desc: "Nihai işlem tarihidir." },
    { name: "Son Yönlendiren", desc: "Başvuruyu başka birime en son yönlendiren kullanıcıdır." },
    { name: "Son Yönlendirilen Birim", desc: "Başvurunun son gönderildiği birimdir." },
    { name: "Son Yönlendirme Notu", desc: "En son yönlendirme sırasında yazılan nottur." },
    { name: "Kapatma Talebi Durumu", desc: "Close approval süreci varsa talebin beklemede / onaylandı / reddedildi / iptal edildi bilgisidir." },
    { name: "Talebi Açan", desc: "Kapatma talebini oluşturan kullanıcıdır." },
    { name: "Kararı Veren", desc: "Kapatma talebine nihai kararı veren kullanıcıdır." },
    { name: "Nihai Sonuç", desc: "Close approval sonrasında kabul edilen nihai başvuru sonucudur." },
    { name: "Vatandaşa Giden Son Not", desc: "Vatandaşa gösterilen / giden son geçerli nottur. Klasik kapanış veya kapatma onay sürecinden gelebilir." },
    { name: "Son Notu Giren / Belirleyen", desc: "Vatandaşa giden son notun son halini belirleyen kullanıcıdır." },
    { name: "Uygulama Tarihi", desc: "Onaylanan kapatma kararının başvuruya işlendiği tarihtir." },
    { name: "Kapanış Akışı", desc: "Başvurunun doğrudan mı, yoksa kapatma talebi üzerinden mi kapandığını özetler." },
  ];

  const statusLogColumns = [
    { name: "Zaman", desc: "Durum değişikliğinin gerçekleştiği andır." },
    { name: "İşlemi Yapan", desc: "Durumu değiştiren kullanıcıdır." },
    { name: "Durum", desc: "Yeni verilen başvuru durumudur." },
    { name: "Not", desc: "Durum değişikliği sırasında girilen açıklamadır." },
  ];

  const forwardLogColumns = [
    { name: "Zaman", desc: "Yönlendirme işleminin tarih/saat bilgisidir." },
    { name: "İşlemi Yapan", desc: "Yönlendirmeyi yapan kullanıcıdır." },
    { name: "Kaynak Birim", desc: "Başvurunun çıktığı birimdir." },
    { name: "Hedef Birim", desc: "Başvurunun gönderildiği birimdir." },
    { name: "Not", desc: "Yönlendirme sırasında yazılan açıklamadır." },
  ];

  const operationLogColumns = [
    { name: "Zaman", desc: "İşlem log kaydının zamanıdır." },
    { name: "İşlemi Yapan", desc: "İşlemi gerçekleştiren kullanıcıdır." },
    { name: "İşlem", desc: "Kaydedilen operasyon aksiyonudur." },
    { name: "Detay", desc: "İşleme ait ek meta / açıklama bilgisidir." },
  ];

  const closeHistoryColumns = [
    { name: "Talebi Açan", desc: "Kapatma talebini oluşturan kullanıcıdır." },
    { name: "Talep Tarihi", desc: "Talebin sisteme kaydedildiği tarihtir." },
    { name: "Önerilen Sonuç", desc: "Talep açılırken önerilen başvuru sonucudur." },
    { name: "Önerilen Not", desc: "Talep açılırken önerilen vatandaş notudur." },
    { name: "İç Not", desc: "Kurum içi değerlendirme için kullanılan nottur." },
    { name: "Kararı Veren", desc: "Talebe onay / red / iptal kararı veren kullanıcıdır." },
    { name: "Karar", desc: "Onaylandı, reddedildi veya iptal edildi bilgisidir." },
    { name: "Karar Tarihi", desc: "Nihai kararın verildiği tarihtir." },
    { name: "Karar Notu", desc: "Karar aşamasında yazılan açıklamadır." },
    { name: "Nihai Sonuç", desc: "Karar sonrası kabul edilen başvuru sonucudur." },
    { name: "Vatandaşa Giden Not", desc: "Karar sonrası vatandaşa gösterilecek son nottur." },
    { name: "Uygulayan", desc: "Onaylı kararın başvuruya işlendiği kullanıcıdır." },
    { name: "Uygulama Tarihi", desc: "Kararın sisteme uygulandığı tarihtir." },
    { name: "Sonuç Değişti Mi", desc: "İlk öneri ile nihai karar arasında sonuç değişikliği olup olmadığını gösterir." },
    { name: "Not Değişti Mi", desc: "İlk öneri notu ile nihai vatandaş notu arasında değişiklik olup olmadığını gösterir." },
  ];

  return (
    <div className="reportv1-helpv2-content">
      <Section title="Bu ekranın çalışma mantığı" icon={SparklesIcon}>
        <div className="reportv1-helpv2-grid reportv1-helpv2-grid--2">
          <div className="reportv1-helpv2-note">
            <div className="reportv1-helpv2-note__title">1. Filtre seç</div>
            <div className="reportv1-helpv2-note__text">
              Tarih, birim, mahalle, durum, takip no, arama ve varsa gelişmiş filtreleri girersiniz.
            </div>
          </div>

          <div className="reportv1-helpv2-note">
            <div className="reportv1-helpv2-note__title">2. Uygula</div>
            <div className="reportv1-helpv2-note__text">
              Sonuçlar yalnızca <strong>Uygula</strong> sonrası yenilenir. Taslak değişiklikler otomatik sorgu çalıştırmaz.
            </div>
          </div>

          <div className="reportv1-helpv2-note">
            <div className="reportv1-helpv2-note__title">3. Tabloyu incele</div>
            <div className="reportv1-helpv2-note__text">
              Yetkinize göre başvurular, loglar ve kapatma talep geçmişi sekmeler halinde görünür.
            </div>
          </div>

          <div className="reportv1-helpv2-note">
            <div className="reportv1-helpv2-note__title">4. Excel indir</div>
            <div className="reportv1-helpv2-note__text">
              Ekranda gördüğünüz sorgu mantığıyla uyumlu export alınır.
            </div>
          </div>
        </div>
      </Section>

      <Section title="Rol bazlı kullanım farkı" icon={ShieldCheckIcon}>
        <div className="reportv1-helpv2-grid reportv1-helpv2-grid--2">
          <RoleCard
            title="Başkan"
            icon={UserCircleIcon}
            items={[
              "Temel başvuru tablosuna ek olarak gelişmiş filtreleri açabilir.",
              canLogs
                ? "Durum Logları, Yönlendirme Logları ve İşlem Logları dataset’lerini kullanabilir."
                : "Log dataset’leri bu yapılandırmada kapalı görünüyor.",
              closeApprovalEnabled
                ? "Kapatma Talep Geçmişi dataset’ini ve close approval filtrelerini kullanabilir."
                : "Close approval dataset’i bu yapılandırmada kapalı görünüyor.",
              canMultiUnit
                ? "Birden fazla birim veya tüm birimler üzerinde çalışabilir."
                : "Birim seçimi sınırlı olabilir.",
              "Tek sheet veya çoklu sheet export alabilir.",
            ]}
          />

          <RoleCard
            title="Müdür / Birim Personeli"
            icon={UserCircleIcon}
            items={[
              "Temel kullanım odağı Başvurular tablosudur.",
              "Genelde aktif birimi üzerinden rapor alır.",
              "Görünür dataset sayısı başkana göre daha sınırlıdır.",
              "Klasik inceleme / sonuçlandırma takibi için başvurular tablosu ana çalışma alanıdır.",
              "Export tarafında daha sade çıktı görür.",
            ]}
          />
        </div>
      </Section>

      <Section title="Filtre alanları ne işe yarar?" icon={FunnelIcon}>
        <div className="reportv1-helpv2-grid reportv1-helpv2-grid--2">
          <div className="reportv1-helpv2-card">
            <InfoRow label="Tarih Aralığı" value="Sorgunun kapsayacağı başlangıç ve bitiş tarihini belirler." />
            <InfoRow label="Tarih Modu" value="Başvuru tarihine göre mi, olay/log zamanına göre mi filtreleneceğini belirler." />
            <InfoRow label="Birim" value="Yetkiye göre tek birim, seçili birimler veya tüm birimler üzerinde çalışır." />
            <InfoRow label="Mahalle" value="Başvurunun mahalle kaydına göre daraltma yapar." />
            <InfoRow label="Durum" value="Yeni, incelemeye alındı, çözüldü, çözülemedi gibi başvuru durumlarını filtreler." />
          </div>

          <div className="reportv1-helpv2-card">
            <InfoRow label="Takip No" value="Belirli bir başvuruyu doğrudan bulmak için kullanılır." />
            <InfoRow label="Arama" value="Adres, mahalle, konu veya benzeri metin alanlarında arama yapar." />
            <InfoRow label="Başvuru ID" value="Belirli kayıtları teknik ID ile hedeflemek için kullanılır." />
            <InfoRow label="Gelişmiş Filtreler" value="Kimin incelediği, kimin yönlendirdiği, kimin onay verdiği gibi detayları süzer." />
          </div>
        </div>
      </Section>

      <Section title="Başvurular tablosu neyi gösterir?" icon={TableCellsIcon}>
        <p className="reportv1-helpv2-paragraph">
          Bu tablo ana çalışma ekranıdır. Bir başvurunun yaşam döngüsünü özet biçimde görürsünüz:
          oluşturulma, işleme alınma, yönlendirme, sonuçlandırma ve varsa kapatma onay süreci.
        </p>

        <ColumnTable rows={applicationColumns} />
      </Section>

      {canLogs ? (
        <Section title="Log tabloları ne işe yarar?" icon={ClipboardDocumentListIcon}>
          <div className="reportv1-helpv2-stacked">
            <div className="reportv1-helpv2-card">
              <div className="reportv1-helpv2-card__title">Durum Logları</div>
              <p className="reportv1-helpv2-card__text">
                Başvurunun durum değişikliklerini zaman sırasıyla gösterir.
              </p>
              <ColumnTable rows={statusLogColumns} />
            </div>

            <div className="reportv1-helpv2-card">
              <div className="reportv1-helpv2-card__title">Yönlendirme Logları</div>
              <p className="reportv1-helpv2-card__text">
                Başvurunun hangi birimden hangi birime, kim tarafından yönlendirildiğini gösterir.
              </p>
              <ColumnTable rows={forwardLogColumns} />
            </div>

            <div className="reportv1-helpv2-card">
              <div className="reportv1-helpv2-card__title">İşlem Logları</div>
              <p className="reportv1-helpv2-card__text">
                Sistem tarafından kaydedilen operasyon adımlarını ve teknik işlem özetlerini gösterir.
              </p>
              <ColumnTable rows={operationLogColumns} />
            </div>
          </div>
        </Section>
      ) : null}

      {closeApprovalEnabled ? (
        <Section title="Kapatma Talep Geçmişi nasıl okunur?" icon={DocumentCheckIcon}>
          <p className="reportv1-helpv2-paragraph">
            Bu tablo, başvurunun close approval sürecini adım adım gösterir. En önemli kullanım amacı şudur:
            <strong> kim talebi açtı, ne önerdi, kim karar verdi, vatandaş notu değişti mi, karar sisteme kim tarafından işlendi?</strong>
          </p>

          <ColumnTable rows={closeHistoryColumns} />
        </Section>
      ) : null}

      <Section title="Export (Excel) mantığı" icon={ArrowDownTrayIcon}>
        <div className="reportv1-helpv2-grid reportv1-helpv2-grid--2">
          <div className="reportv1-helpv2-card">
            <div className="reportv1-helpv2-card__title">Temel mantık</div>
            <ul className="reportv1-helpv2-list">
              <Bullet>Export, son başarılı sorgu mantığına göre alınır.</Bullet>
              <Bullet>Yani önce filtreleri uygular, sonra export alırsınız.</Bullet>
              <Bullet>Tek sheet veya çoklu sheet export seçimi role ve moda göre değişebilir.</Bullet>
            </ul>
          </div>

          <div className="reportv1-helpv2-card">
            <div className="reportv1-helpv2-card__title">Bu yapıda görülen export modları</div>
            <div className="reportv1-helpv2-chip-row">
              {exportModes.length ? (
                exportModes.map((m, idx) => <Chip key={idx}>{m}</Chip>)
              ) : (
                <Chip>Liste Excel</Chip>
              )}
            </div>
          </div>
        </div>
      </Section>

      <Section title="Kullanım önerileri" icon={CheckCircleIcon} subtle>
        <div className="reportv1-helpv2-grid reportv1-helpv2-grid--2">
          <div className="reportv1-helpv2-card">
            <div className="reportv1-helpv2-card__title">Başkan için öneri</div>
            <ul className="reportv1-helpv2-list">
              <Bullet>Önce Başvurular tablosu ile genel görünümü kontrol edin.</Bullet>
              <Bullet>Sonra gerekliyse log dataset’lerini açarak ayrıntıya inin.</Bullet>
              <Bullet>Close approval kullanılan kayıtlarda mutlaka Kapatma Talep Geçmişi’ne bakın.</Bullet>
            </ul>
          </div>

          <div className="reportv1-helpv2-card">
            <div className="reportv1-helpv2-card__title">Müdür / Birim personeli için öneri</div>
            <ul className="reportv1-helpv2-list">
              <Bullet>Takip No ve arama alanlarını birlikte kullanmak çoğu zaman en hızlı yöntemdir.</Bullet>
              <Bullet>Önce tarih ve durum ile veri daraltıp sonra arama yapmak daha verimli olur.</Bullet>
              <Bullet>Vatandaşa giden son not ve sonuçlandırma alanları günlük iş takibinde en kritik alanlardır.</Bullet>
            </ul>
          </div>
        </div>
      </Section>
    </div>
  );
};

/* =============================================================================
   Modal sarmalayıcı (eski kullanım — geriye dönük uyum için korunur)
============================================================================= */

const ReportHelpDrawer = ({ open, onClose, config }) => {
  const onKeyDown = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === "Escape") onClose?.();
    },
    [open, onClose]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onKeyDown]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!open) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="reportv1-helpv2-root" role="dialog" aria-modal="true">
      <div
        className="reportv1-helpv2-backdrop"
        onClick={() => onClose?.()}
        aria-hidden="true"
      />

      <div className="reportv1-helpv2-modal">
        <div className="reportv1-helpv2-header">
          <div className="reportv1-helpv2-header__left">
            <div className="reportv1-helpv2-badge">
              <InformationCircleIcon className="reportv1-helpv2-badge__icon" />
              <span>Yardım</span>
            </div>

            <div>
              <h2 className="reportv1-helpv2-title">Rapor Ekranı Kullanım Rehberi</h2>
              <p className="reportv1-helpv2-subtitle">
                Bu ekran; filtreleme, sonuç inceleme, log takibi ve Excel export işlemlerini
                tek yerden yönetmek için kullanılır.
              </p>
            </div>
          </div>

          <button
            type="button"
            className="btn-md btn-md-ghost reportv1-helpv2-close"
            onClick={() => onClose?.()}
            aria-label="Kapat"
            title="Kapat (ESC)"
          >
            <XMarkIcon width={18} height={18} />
          </button>
        </div>

        <ReportHelpContent config={config} />

        <div className="reportv1-helpv2-footer">
          <button
            type="button"
            className="btn-md btn-md-primary"
            onClick={() => onClose?.()}
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportHelpDrawer;
